import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyTechnicianProfile,
  fetchTechnician,
  fetchTechnicians,
  createTechnician,
  updateTechnician,
  type MyTechnicianProfile,
  type Technician,
  type TechnicianDirectoryPage,
  type TechnicianFilters,
  type CreateTechnicianInput,
  type UpdateTechnicianInput,
} from '../api/techniciansApi';
import { samePopulation } from '../utils/samePopulation';

const KEY = 'technicians';

// A write moves the referential screens too: a specialty's technician count is derived from the
// links this record owns. Invalidating both trees keeps them from disagreeing.
const useTechnicianMutation = <TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['referentials'] });
    },
  });
};

/**
 * The directory page. Previous rows stay for a page turn and nothing else: keeping them
 * unconditionally held `isLoading` at false, so a filter change left the previous technicians in
 * the table with no loader. `enabled` is for the callers that suggest rather than list, which
 * would otherwise fetch the whole pool the moment their field opened, empty.
 */
export const useTechniciansList = (filters: TechnicianFilters, enabled = true) =>
  useQuery<TechnicianDirectoryPage>({
    queryKey: [KEY, 'list', filters],
    queryFn: () => fetchTechnicians(filters),
    placeholderData: (previous: TechnicianDirectoryPage | undefined, previousQuery) =>
      samePopulation('page', previousQuery?.queryKey?.[2] as TechnicianFilters | undefined, filters)
        ? previous
        : undefined,
    staleTime: 30_000,
    enabled,
  });

/** `enabled` is false while creating, where there is no id to read. */
export const useTechnician = (id: number | null) =>
  useQuery<Technician>({
    queryKey: [KEY, id],
    queryFn: () => fetchTechnician(id as number),
    enabled: id !== null,
  });

export const useCreateTechnician = () =>
  useTechnicianMutation((input: CreateTechnicianInput) => createTechnician(input));

export const useUpdateTechnician = () =>
  useTechnicianMutation(({ id, data }: { id: number; data: UpdateTechnicianInput }) => updateTechnician(id, data));

/**
 * The technician's own identity, as the back office keeps it. No mutation stands beside it (D11),
 * and no `enabled`: the screen that reads it is his own, so there is never an id to wait for.
 */
export const useMyTechnicianProfile = () =>
  useQuery<MyTechnicianProfile>({
    queryKey: [KEY, 'me'],
    queryFn: fetchMyTechnicianProfile,
  });

